import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Cake as BirthdayIcon,
  AssignmentInd as DocumentIcon,
  Star as StarIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import AddressFormModal from "./AddressFormModal";
import CustomerIdentificationModal from "../CustomerIdentificationModal";
import {
  addCustomerAddress,
  updateCustomerAddress,
  deleteCustomerAddress,
  setDefaultAddress,
} from "../../services/pharmaApi";

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  infoCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  infoLabel: {
    color: theme.palette.text.secondary,
    minWidth: 100,
    fontSize: "0.875rem",
  },
  infoValue: {
    fontWeight: 500,
  },
  addressCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    "&.default": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light + "10",
    },
  },
  addressLabel: {
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  defaultBadge: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: "0.7rem",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  warningBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning.light + "20",
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  chipContainer: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  addressActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    flexShrink: 0,
  },
  actionButton: {
    padding: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  addButton: {
    textTransform: "none",
  },
}));

const formatCPF = (cpf) => {
  if (!cpf) return null;
  const digits = cpf.replace(/\D/g, "");
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatPhone = (phone) => {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString("pt-BR");
};

const CustomerDataTab = ({ customer, contact, onCustomerUpdate }) => {
  const classes = useStyles();
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [savingAddress, setSavingAddress] = useState(false);
  const [deletingAddressId, setDeletingAddressId] = useState(null);
  const [customerFormOpen, setCustomerFormOpen] = useState(false);

  if (!customer) {
    return (
      <Box className={classes.emptyState}>
        <PersonIcon style={{ fontSize: 48, opacity: 0.5 }} />
        <Typography variant="body1">Dados do cliente não disponíveis</Typography>
      </Box>
    );
  }

  const isNewCustomer = !customer.id || customer.isNew;

  // Build address from contact (Whaticket) if customer has no addresses
  const contactAddress = contact?.street ? {
    id: 'contact-address',
    label: 'Endereco Principal',
    street: contact.street,
    number: contact.streetNumber,
    complement: contact.complement,
    neighborhood: contact.neighborhood,
    city: contact.city,
    state: contact.state,
    zipcode: contact.zipCode || contact.cep,
    reference: contact.addressReference,
    isDefault: true,
  } : null;

  // Use customer addresses if available, otherwise use contact address
  const addresses = customer.addresses && customer.addresses.length > 0
    ? customer.addresses
    : contactAddress ? [contactAddress] : [];

  const handleOpenAddAddress = () => {
    setEditingAddress(null);
    setAddressModalOpen(true);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressModalOpen(true);
  };

  const handleSaveContactAddress = () => {
    // Pre-fill the form with contact address data
    setEditingAddress(contactAddress);
    setAddressModalOpen(true);
  };

  const handleCloseAddressModal = () => {
    setAddressModalOpen(false);
    setEditingAddress(null);
  };

  const handleSaveAddress = async (addressData) => {
    if (!customer?.id) {
      toast.error("Cliente precisa ser cadastrado primeiro");
      return;
    }

    setSavingAddress(true);
    try {
      if (addressData.id && addressData.id !== 'contact-address') {
        // Update existing address
        await updateCustomerAddress(customer.id, addressData.id, addressData);
        toast.success("Endereço atualizado com sucesso!");
      } else {
        // Create new address
        await addCustomerAddress(customer.id, addressData);
        toast.success("Endereço adicionado com sucesso!");
      }

      // Refresh customer data
      if (onCustomerUpdate) {
        onCustomerUpdate();
      }

      handleCloseAddressModal();
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error("Erro ao salvar endereço");
    } finally {
      setSavingAddress(false);
    }
  };

  const handleDeleteAddress = async (address) => {
    if (!customer?.id || !address.id || address.id === 'contact-address') {
      return;
    }

    if (!window.confirm("Tem certeza que deseja excluir este endereço?")) {
      return;
    }

    setDeletingAddressId(address.id);
    try {
      await deleteCustomerAddress(customer.id, address.id);
      toast.success("Endereço excluído com sucesso!");

      if (onCustomerUpdate) {
        onCustomerUpdate();
      }
    } catch (error) {
      console.error("Error deleting address:", error);
      toast.error("Erro ao excluir endereço");
    } finally {
      setDeletingAddressId(null);
    }
  };

  const handleSetDefault = async (address) => {
    if (!customer?.id || !address.id || address.id === 'contact-address') {
      return;
    }

    try {
      await setDefaultAddress(customer.id, address.id);
      toast.success("Endereço definido como principal!");

      if (onCustomerUpdate) {
        onCustomerUpdate();
      }
    } catch (error) {
      console.error("Error setting default address:", error);
      toast.error("Erro ao definir endereço principal");
    }
  };

  const handleRegisterCustomer = async () => {
    // Open customer form modal for registration
    setCustomerFormOpen(true);
  };

  const handleEditCustomer = () => {
    setCustomerFormOpen(true);
  };

  const handleCustomerIdentified = (updatedContact) => {
    // Called when CustomerIdentificationModal saves successfully
    console.log("Customer identified:", updatedContact);
    setCustomerFormOpen(false);

    if (onCustomerUpdate) {
      onCustomerUpdate();
    }
  };

  return (
    <Box>
      {isNewCustomer && (
        <Box className={classes.warningBox}>
          <WarningIcon color="warning" />
          <Box flex={1}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              Cliente não cadastrado no sistema
            </Typography>
            <Typography variant="caption">
              Cadastre o cliente para poder finalizar vendas e acessar histórico.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleRegisterCustomer}
            startIcon={<PersonIcon />}
          >
            Cadastrar
          </Button>
        </Box>
      )}

      {/* Basic Info */}
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            <PersonIcon color="primary" />
            Informações Básicas
          </Typography>
          <IconButton
            size="small"
            title={customer?.id ? "Editar dados do cliente" : "Cadastrar cliente"}
            onClick={handleEditCustomer}
            className={classes.actionButton}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
        <Paper className={classes.infoCard} elevation={0} variant="outlined">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Nome:</Typography>
                <Typography className={classes.infoValue}>
                  {customer.name || contact?.name || "-"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>WhatsApp:</Typography>
                <Typography className={classes.infoValue}>
                  {formatPhone(customer.whatsapp || contact?.number) || "-"}
                </Typography>
              </Box>
            </Grid>
            {customer.phone && customer.phone !== customer.whatsapp && (
              <Grid item xs={12} sm={6}>
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>Telefone:</Typography>
                  <Typography className={classes.infoValue}>
                    {formatPhone(customer.phone)}
                  </Typography>
                </Box>
              </Grid>
            )}
            {customer.email && (
              <Grid item xs={12} sm={6}>
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>E-mail:</Typography>
                  <Typography className={classes.infoValue}>{customer.email}</Typography>
                </Box>
              </Grid>
            )}
            {customer.cpf && (
              <Grid item xs={12} sm={6}>
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>CPF:</Typography>
                  <Typography className={classes.infoValue}>
                    {formatCPF(customer.cpf)}
                  </Typography>
                </Box>
              </Grid>
            )}
            {customer.birthDate && (
              <Grid item xs={12} sm={6}>
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>Nascimento:</Typography>
                  <Typography className={classes.infoValue}>
                    {formatDate(customer.birthDate)}
                  </Typography>
                </Box>
              </Grid>
            )}
            {customer.gender && (
              <Grid item xs={12} sm={6}>
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>Gênero:</Typography>
                  <Typography className={classes.infoValue}>
                    {customer.gender === "M" ? "Masculino" : customer.gender === "F" ? "Feminino" : "Outro"}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          <Box className={classes.chipContainer}>
            {customer.acceptsMarketing && (
              <Chip size="small" label="Aceita marketing" color="primary" variant="outlined" />
            )}
            {customer.isActive === false && (
              <Chip size="small" label="Inativo" color="secondary" />
            )}
          </Box>
        </Paper>
      </Box>

      {/* Addresses */}
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            <LocationIcon color="primary" />
            Endereços
          </Typography>
          {customer?.id && (
            <Button
              size="small"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenAddAddress}
              className={classes.addButton}
            >
              Adicionar
            </Button>
          )}
        </Box>

        {addresses && addresses.length > 0 ? (
          addresses.map((address) => (
            <Box
              key={address.id}
              className={`${classes.addressCard} ${address.isDefault ? "default" : ""}`}
            >
              <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                <Box flex={1}>
                  <Typography className={classes.addressLabel}>
                    {address.label || "Endereço"}
                    {address.isDefault && (
                      <Chip size="small" label="Principal" className={classes.defaultBadge} />
                    )}
                  </Typography>
                  <Typography variant="body2">
                    {address.street}, {address.number}
                    {address.complement && ` - ${address.complement}`}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {address.neighborhood} - {address.city}/{address.state}
                  </Typography>
                  {address.zipcode && (
                    <Typography variant="body2" color="textSecondary">
                      CEP: {address.zipcode}
                    </Typography>
                  )}
                  {address.reference && (
                    <Typography variant="caption" color="textSecondary">
                      Ref: {address.reference}
                    </Typography>
                  )}
                </Box>
                {customer?.id && (
                  <Box className={classes.addressActions}>
                    {address.id !== 'contact-address' ? (
                      <>
                        {!address.isDefault && (
                          <IconButton
                            size="small"
                            title="Definir como principal"
                            onClick={() => handleSetDefault(address)}
                            className={classes.actionButton}
                          >
                            <StarIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          title="Editar endereço"
                          onClick={() => handleEditAddress(address)}
                          className={classes.actionButton}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Excluir endereço"
                          onClick={() => handleDeleteAddress(address)}
                          disabled={deletingAddressId === address.id}
                          className={classes.actionButton}
                        >
                          {deletingAddressId === address.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </>
                    ) : (
                      <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleSaveContactAddress}
                      >
                        Salvar
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          ))
        ) : (
          <Box className={classes.emptyState}>
            <LocationIcon style={{ fontSize: 32, opacity: 0.5 }} />
            <Typography variant="body2">Nenhum endereço cadastrado</Typography>
            {customer?.id && (
              <Button
                size="small"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAddAddress}
                style={{ marginTop: 8 }}
              >
                Adicionar Endereço
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* System Info */}
      {customer.id && (
        <Box className={classes.section}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Informações do Sistema
          </Typography>
          <Paper className={classes.infoCard} elevation={0} variant="outlined">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>ID Cliente:</Typography>
                  <Typography variant="caption" className={classes.infoValue}>
                    {customer.id}
                  </Typography>
                </Box>
              </Grid>
              {customer.whaticketContactId && (
                <Grid item xs={12} sm={6}>
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>ID Contato:</Typography>
                    <Typography variant="caption" className={classes.infoValue}>
                      {customer.whaticketContactId}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {customer.createdAt && (
                <Grid item xs={12} sm={6}>
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>Cadastrado em:</Typography>
                    <Typography className={classes.infoValue}>
                      {formatDate(customer.createdAt)}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {customer.lastOrderAt && (
                <Grid item xs={12} sm={6}>
                  <Box className={classes.infoRow}>
                    <Typography className={classes.infoLabel}>Último pedido:</Typography>
                    <Typography className={classes.infoValue}>
                      {formatDate(customer.lastOrderAt)}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Box>
      )}

      {/* Address Form Modal */}
      <AddressFormModal
        open={addressModalOpen}
        onClose={handleCloseAddressModal}
        onSave={handleSaveAddress}
        address={editingAddress}
        customerId={customer?.id}
        isLoading={savingAddress}
      />

      {/* Customer Identification Modal - same as used in sales flow */}
      <CustomerIdentificationModal
        open={customerFormOpen}
        onClose={() => setCustomerFormOpen(false)}
        contact={contact}
        contactId={contact?.id}
        onIdentified={handleCustomerIdentified}
      />
    </Box>
  );
};

export default CustomerDataTab;
